/**
 * ST-05 (AIVA-6805) — the pane's one "All actions" palette: PowerPoint's own
 * actions, plus Deckorate's design actions when the persona has Deckorate.
 *
 * Opens exactly like Deckorate's palette always has: an Office Dialog where
 * the host has the Dialog API, the in-pane overlay where it doesn't — or when
 * the dialog fails to open. Running a command maps it onto handles TaskPane
 * already owns; nothing here talks to the backend.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeckorateCommand } from "../../../../deckorate/types";
import {
  canUseDialog,
  openAllActionsDialog,
} from "../../../../deckorate/features/command-palette/hooks/openAllActionsDialog";
import { requestChatSheet } from "../../../../deckorate/features/action-chips/services/chatSheetBridge";
import { buildAllActionsCatalog, encodeCatalogSources, type CatalogSources } from "../services/allActionsCatalog";
import { setAllActionsOpen, subscribeAllActions } from "../services/allActionsBridge";
import type { PaletteSkill } from "../services/paletteItems";
import type { PaletteAction } from "../types";
import { usePaletteShortcut } from "./usePaletteShortcut";

export interface PaletteHandlers {
  readonly sendPrompt: (text: string) => void;
  readonly prefill: (text: string) => void;
  readonly openActions: () => void;
  readonly newChat: () => void;
  readonly openSettings: () => void;
}

export interface UseAllActionsPaletteOptions {
  /** PowerPoint only. */
  readonly enabled: boolean;
  /** Deckorate is in the module list: its design actions join the list. */
  readonly deckorateEnabled: boolean;
  /** Read when the palette opens, so a skill toggled in Settings shows at once. */
  readonly getSkills: () => readonly PaletteSkill[];
  readonly isBlocked: () => boolean;
  readonly handlers: PaletteHandlers;
  /** A Deckorate design action — it runs inside the Deckorate tab. */
  readonly runDeckorateCommand: (command: DeckorateCommand) => void;
}

/**
 * Focus the chat composer with the cursor after its text. Deferred so it runs
 * after the palette has closed and handed focus back to where it was.
 */
export function focusComposerSoon(): void {
  setTimeout(() => {
    const composer = document.querySelector<HTMLTextAreaElement>('[data-composer="chat"]');
    if (!composer) return;
    composer.focus();
    composer.setSelectionRange(composer.value.length, composer.value.length);
  }, 0);
}

export function runPaletteAction(action: PaletteAction, handlers: PaletteHandlers): void {
  switch (action.kind) {
    case "prompt":
      handlers.sendPrompt(action.text);
      return;
    case "prefill":
      handlers.prefill(action.text);
      return;
    case "open-actions":
      handlers.openActions();
      return;
    case "new-chat":
      handlers.newChat();
      return;
    case "settings":
      handlers.openSettings();
      return;
  }
}

export function useAllActionsPalette(options: UseAllActionsPaletteOptions) {
  // Non-null while the in-pane palette is showing: the list it was opened with.
  const [sources, setSources] = useState<CatalogSources | null>(null);
  const dialogOpenRef = useRef(false);
  // Read through a ref so open/run keep one identity: the bridge and the
  // shortcut listener subscribe once, not on every TaskPane render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const run = useCallback((command: DeckorateCommand) => {
    const { handlers, runDeckorateCommand } = optionsRef.current;
    if (command.kind === "aiva") {
      if (command.aivaAction) runPaletteAction(command.aivaAction, handlers);
      return;
    }
    // The sheet lives on the chat tab; the bridge's subscribers switch to it.
    if (command.kind === "open-sheet" && command.sheet) {
      requestChatSheet(command.sheet);
      return;
    }
    runDeckorateCommand(command);
  }, []);

  const close = useCallback(() => {
    setSources(null);
    setAllActionsOpen(false);
  }, []);

  const showInPane = useCallback((next: CatalogSources) => {
    setAllActionsOpen(true);
    setSources(next);
  }, []);

  const open = useCallback(() => {
    const { enabled, isBlocked, getSkills, deckorateEnabled } = optionsRef.current;
    if (!enabled || isBlocked() || dialogOpenRef.current) return;
    const next: CatalogSources = { skills: getSkills(), deckorate: deckorateEnabled };
    if (!canUseDialog()) {
      showInPane(next);
      return;
    }
    dialogOpenRef.current = true;
    setAllActionsOpen(true);
    void (async () => {
      try {
        const command = await openAllActionsDialog(encodeCatalogSources(next));
        setAllActionsOpen(false);
        if (command) run(command);
      } catch {
        // Already logged by openAllActionsDialog; the overlay still works.
        showInPane(next);
      } finally {
        dialogOpenRef.current = false;
      }
    })();
  }, [run, showInPane]);

  const isOpen = options.enabled && sources !== null;
  const toggle = useCallback(() => (sources ? close() : open()), [sources, open, close]);

  usePaletteShortcut(options.enabled, toggle, options.isBlocked);

  // Deckorate's home "All actions" card.
  useEffect(() => subscribeAllActions(open), [open]);

  const catalog = useMemo(() => (sources ? buildAllActionsCatalog(sources) : []), [sources]);

  const runAndClose = useCallback(
    (command: DeckorateCommand) => {
      close();
      run(command);
    },
    [close, run],
  );

  return { isOpen, open, close, catalog, showCodes: sources?.deckorate ?? false, run: runAndClose };
}
